import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Phone, Mail, Building2, Truck } from "lucide-react";
import { displayCompanyName } from "@/lib/displayCompanyName";

interface Viewer {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  company_name: string | null;
  mc_number: string | null;
  dot_number: string | null;
  viewed_at: string;
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function LoadViewersDialog({
  loadId,
  loadRef,
  open,
  onOpenChange,
}: {
  loadId: string;
  loadRef: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [viewers, setViewers] = useState<Viewer[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !loadId) return;
    setLoading(true);
    supabase
      .rpc("get_load_viewer_details", { p_load_id: loadId })
      .then(({ data, error }) => {
        if (!error && data) setViewers(data as Viewer[]);
        setLoading(false);
      });
  }, [open, loadId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[70vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2">
            <Truck className="h-4 w-4 text-accent" />
            Carrier Views — {loadRef}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Carriers who viewed this load. Click to call or email.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-2 px-2">
          {loading ? (
            <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-xs">Loading viewers…</span>
            </div>
          ) : viewers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-xs">
              No carrier views yet.
            </div>
          ) : (
            <div className="space-y-2">
              {viewers.map((v) => {
                const name = [v.first_name, v.last_name].filter(Boolean).join(" ") || "Unknown";
                return (
                  <div
                    key={v.user_id}
                    className="rounded-lg border bg-card p-3 space-y-1.5"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-semibold text-foreground">{name}</p>
                        {v.company_name && (
                          <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {displayCompanyName(v.company_name)}
                          </p>
                        )}
                      </div>
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                        {timeAgo(v.viewed_at)}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-[11px]">
                      {v.mc_number && (
                        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-muted-foreground">
                          MC-{v.mc_number}
                        </span>
                      )}
                      {v.dot_number && (
                        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-muted-foreground">
                          DOT-{v.dot_number}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3 pt-1">
                      {v.phone && (
                        <a
                          href={`tel:${v.phone}`}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-accent hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Phone className="h-3 w-3" />
                          {v.phone}
                        </a>
                      )}
                      {v.email && (
                        <a
                          href={`mailto:${v.email}`}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-accent hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Mail className="h-3 w-3" />
                          {v.email}
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
